import React, { useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-hot-toast";
import { Mail } from "lucide-react"; // add this at the top

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const toyData = [
  { id: "toy1", src: "Group71.png", price: 480 },
];

function CheckoutPopup({ selectedToy, step, setStep, quantity, setQuantity, resetPopup }) {
  const stripe = useStripe();
  const elements = useElements();

  const [address, setAddress] = useState("");
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [processing, setProcessing] = useState(false);

  const totalAmount = selectedToy.price * quantity;
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const handleCardPayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error("Stripe.js not loaded yet.");
      return;
    }
    if (!cardNumberComplete || !cardCvcComplete || !cardExpiryComplete) {
      toast.error("Please complete all card fields.");
      return;
    }
    if (!address.trim()) {
      toast.error("Please enter your shipping address.");
      return;
    }

    setProcessing(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, "");
      const response = await fetch(`${apiBaseUrl}/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          toyId: selectedToy.id,
          quantity,
          address,
          email: user.email || "",
          fullName: user.full_name || "Unnamed User",
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.clientSecret) {
        toast.error(data.error || "Payment creation failed");
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: user.full_name || "Customer",
            email: user.email || undefined,
            address: { line1: address },
          },
        },
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        await fetch(`${apiBaseUrl}/api/payment-success`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
        });

        toast.success("Payment successful! 🎉");
        setStep(4);
      } else {
        toast.error("Payment failed: " + paymentIntent.status);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="relative rounded-2xl p-6 w-full max-w-md border border-pink-400/40 shadow-xl bg-[#2a2540]">
        <button
          className="absolute top-3 right-3 text-white text-xl"
          onClick={resetPopup}
          disabled={processing}
        >
          ✕
        </button>

        {/* Step 1: Quantity */}
        {step === 1 && (
          <div className="text-center">
            <img
              src={selectedToy.src}
              alt="Toy"
              className="mx-auto w-40 h-40 md:w-56 md:h-56 mb-4"
            />
            <h2 className="text-lg font-bold text-white mb-3">Choose Quantity</h2>
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-3 py-2 bg-pink-400 rounded text-white"
              >
                -
              </button>
              <span className="text-white text-lg">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-3 py-2 bg-pink-400 rounded text-white"
              >
                +
              </button>
            </div>
            <p className="text-white mb-4">Total: ${totalAmount}</p>
            <button
              onClick={() => setStep(2)}
              className="px-6 py-2 rounded bg-gradient-to-r from-pink-300 to-blue-300 text-white"
            >
              Next
            </button>
          </div>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <div className="text-center text-white ">
            <h2 className="text-lg font-bold mb-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]">
                Shipping Address
              </span>
            </h2>

            <div className="frame-11g relative">
              <textarea
                className="w-full p-3 rounded bg-transparent text-white resize-none outline-none border-none relative z-10"
                rows={3}
                placeholder="Enter your address..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <button
              onClick={() => setStep(3)}
              className="px-6 py-2 rounded bg-gradient-to-r from-pink-300 to-blue-300 text-white mt-2"
            >
              Next
            </button>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="text-center text-white">
            <h2 className="text-lg font-bold mb-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]">
                Payment
              </span>
            </h2>

            <form className="w-full space-y-6" onSubmit={handleCardPayment}>
              <div className="relative w-full h-[40px] bg-[#444444] transition-all duration-200 rounded-md">
                <div className="absolute inset-[1px] bg-[#1C1C1C] w-full h-full flex items-center rounded-md">
                  <CardNumberElement
                    className="w-full px-4 rounded-md"
                    options={{
                      style: {
                        base: {
                          color: "#fff",
                          "::placeholder": { color: "#bbb" },
                          iconColor: "#fff",
                        },
                        invalid: { color: "#ff6b6b" },
                      },
                      showIcon: true,
                    }}
                    onChange={(e) => setCardNumberComplete(e.complete)}
                  />
                </div>
              </div>

              <div className="w-full grid grid-cols-2 gap-4">
                <div className="relative w-full h-[40px] bg-[#444444] transition-all duration-200 rounded-md">
                  <div className="absolute inset-[1px] bg-[#1C1C1C] w-full h-full flex items-center rounded-md">
                    <CardCvcElement
                      className="w-full px-4 rounded-md"
                      options={{
                        style: {
                          base: {
                            color: "#fff",
                            "::placeholder": { color: "#bbb" },
                            iconColor: "#fff",
                          },
                          invalid: { color: "#ff6b6b" },
                        },
                        showIcon: true,
                      }}
                      onChange={(e) => setCardCvcComplete(e.complete)}
                    />
                  </div>
                </div>

                <div className="relative w-full h-[40px] bg-[#444444] transition-all duration-200 rounded-md">
                  <div className="absolute inset-[1px] bg-[#1C1C1C] w-full h-full flex items-center rounded-md">
                    <CardExpiryElement
                      className="w-full px-4 rounded-md"
                      options={{
                        style: {
                          base: {
                            color: "#fff",
                            "::placeholder": { color: "#bbb" },
                            iconColor: "#fff",
                          },
                          invalid: { color: "#ff6b6b" },
                        },
                        showIcon: true,
                      }}
                      onChange={(e) => setCardExpiryComplete(e.complete)}
                    />
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={!stripe || processing}
                className="w-full px-6 py-2 rounded bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300 text-white font-semibold hover:opacity-90 transition-opacity"
              >
                {processing ? "Processing..." : `Pay $${totalAmount}`}
              </button>
            </form>
          </div>
        )}

        {/* Step 4: Confirmation */}
      {step === 4 && (
  <div className="text-center text-white px-6 py-4">
    <h2 className="text-xl font-bold mb-4">
    ✅   <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]">
         Order Confirmed
      </span>{" "}
      🎉
    </h2>
    <p className="mb-3">
      Hello{" "}
      <span className="font-semibold">
        {user?.full_name || "Customer"}
      </span>
      ,<br />
      Thank you for placing your order with{" "}
      <span className="font-semibold">BabyU Official Store</span>!
    </p>
    <p className="mb-3">
      You’ve ordered{" "}
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]">
        {quantity}
      </span>{" "}
      toy(s) for{" "}
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]">
        ${totalAmount}
      </span>
      .
    </p>
    <p className="mb-4">
      📦 <span className="font-semibold">Delivery Timeline:</span>  
      Your toy(s) will be delivered within{" "}
      <span className="font-semibold">90 days</span> from your order date.  
      This ensures the highest quality and timely production.
    </p>
    <p className="text-sm opacity-80 mb-4">
      Please keep this confirmation as proof of purchase.  
      For any questions, contact us at{" "}
      <a
        href="mailto:team@babyu.tech"
        className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFA8D1] via-[#C3B1FF] to-[#AEE2FF]"
      >
        team@babyu.tech
      </a>

      
      .
    </p>
    <button
      onClick={resetPopup}
      className="mt-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-400 to-green-500 text-white font-semibold hover:opacity-90 transition"
    >
      Close
    </button>
  </div>
)}

      </div>
    </div>
  );
}

function ToySection() {
  const [selectedToy, setSelectedToy] = useState(null);
  const [step, setStep] = useState(1);
  const [quantity, setQuantity] = useState(1);

  const resetPopup = () => {
    setSelectedToy(null);
    setStep(1);
    setQuantity(1);
  };

  const handleBuy = (toy) => {
    const user = localStorage.getItem("user");
    if (!user) {
      toast.error("Please log in first to buy toys.");
      return;
    }
    setSelectedToy(toy);
  };

  return (
    <section className="relative flex flex-col items-center justify-center  px-4 text-center">
      <header>
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          <span className="bg-gradient-to-r from-pink-300 to-blue-300 bg-clip-text text-transparent">
            Baby Toys Images
          </span>
        </h1>
      </header>

      <div className="grid grid-cols-1 place-items-center">
        {toyData.map((toy, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <div className="w-64 h-64 md:w-96 md:h-96 flex items-center justify-center border border-pink-300 rounded">
              <img
                src={toy.src}
                alt={`Toy ${idx + 1}`}
                className="max-w-full max-h-full"
              />
            </div>
            <p className="mt-4 text-white font-semibold">${toy.price}</p>
            <button
              onClick={() => handleBuy(toy)}
              className="mt-2 px-6 py-2 rounded bg-gradient-to-r from-pink-300 to-blue-300 text-white"
            >
              Buy
            </button>
          </div>
        ))}
      </div>


      {selectedToy && (
        <Elements stripe={stripePromise}>
          <CheckoutPopup
            selectedToy={selectedToy}
            step={step}
            setStep={setStep}
            quantity={quantity}
            setQuantity={setQuantity}
            resetPopup={resetPopup}
          />
        </Elements>
      )}
    </section>
  );
}

export default ToySection;
